import i18n, { type Resource } from 'i18next';
import { initReactI18next } from 'react-i18next';

import Backend from 'i18next-http-backend';

import { DEFAULT_LOCALE, LocaleCode, SUPPORTED_LOCALES } from './locales';
import { resolveLanguage, storeLocale } from './resolve-language';

let initialized = false;

/** Keeps `<html lang>` in step so `:lang()` rules and screen readers follow. */
const trackDocumentLanguage = () => {
  const apply = (language: string) => {
    document.documentElement.lang = language;
  };
  apply(i18n.resolvedLanguage || i18n.language || DEFAULT_LOCALE);
  i18n.on('languageChanged', apply);
};

export interface InitI18nOptions {
  /**
   * Translations bundled into the build, keyed `{ [lng]: { [ns]: keys } }`.
   * The browser extension passes these because it has no locale server to
   * fetch from; the web app leaves them out and loads over HTTP.
   */
  resources?: Resource;
}

/**
 * Initializes the shared i18next instance. The guard makes it safe to call from
 * more than one entry point — the app bootstrap and each Storybook preview —
 * without them reconfiguring each other.
 */
export const initI18n = ({ resources }: InitI18nOptions = {}): typeof i18n => {
  if (initialized) {
    return i18n;
  }
  initialized = true;

  const common = {
    lng: resolveLanguage(),
    fallbackLng: DEFAULT_LOCALE,
    supportedLngs: SUPPORTED_LOCALES.map((locale) => locale.code),
    showSupportNotice: false,
    interpolation: {
      escapeValue: false,
    },
    defaultNS: 'common',
  };

  if (resources) {
    void i18n.use(initReactI18next).init({
      ...common,
      resources,
      ns: Object.keys(resources[DEFAULT_LOCALE] ?? { common: {} }),
    });
  } else {
    void i18n
      .use(Backend)
      .use(initReactI18next)
      .init({
        ...common,
        backend: {
          // Storybook's Vite config defines no `process.env`, so without the
          // fallback the path resolves to the literal "undefined/en/common.json".
          loadPath: `${
            process.env.LOCALIZATION_SERVER || '/locales'
          }/{{lng}}/{{ns}}.json`,
        },
        // Without `ns`, i18next also loads its built-in 'translation' namespace
        // on every init — a 404 for any locale that doesn't carry an empty one.
        // designSystem is here because its components render on every page;
        // errors because its first read is a form's first validation error,
        // mid-interaction, where a lazy load would suspend the whole route.
        ns: ['common', 'designSystem', 'errors'],
      });
  }

  trackDocumentLanguage();

  return i18n;
};

/** Switches language and remembers the choice for the next visit. */
export const setLanguage = async (code: LocaleCode): Promise<void> => {
  await i18n.changeLanguage(code);
  storeLocale(code);
};

export { i18n };
